import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Avatar, ConfigProvider, Form, Input, Tag, Tooltip, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { BaseModal, Button, ListItemInput } from 'components/elements';
import {
  ContactDropdownGlobalStyle,
  ContactOptionMeta,
  ContactOptionName,
  ContactOptionRow,
  ContactOptionText,
  contactModalLightTheme,
} from 'components/elements/chatGroupModal/contactModal.styled';
import { emailValidator } from 'helpers';
import { useSearchUsersLazyQuery, useSendEventInvitationMutation, useUserTokenInfoQuery } from 'graphql/types';
import styled from 'styled-components';
import { colors } from 'settings/style';

const { TextArea } = Input;

interface InvitationModalProps {
  modalVisible?: boolean;
  setModal?: (visible: boolean) => void;
  eventId: string;
  eventTitle?: string | null;
}

interface InvitationFormValues {
  siteMember?: string;
  email: {
    valueLists: string[];
    currentValue: string;
  };
  subject: string;
  message: string;
}

type InvitationUser = {
  id: string;
  username: string;
  email: string;
  firstname?: string;
  lastname?: string;
  avatarUrl?: string;
};

type UserAutoOption = {
  value: string;
  label: React.ReactNode;
  userId: string;
  user: InvitationUser;
};

type SearchUserEntry = {
  id: string;
  name?: string | null;
  username?: string | null;
  email?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  avatarUrl?: string | null;
};

// Matches requested from the server per keystroke, and matches rendered in the dropdown.
const userSearchLimit = 20;
const userOptionLimit = 8;

const toInvitationUser = (entry: SearchUserEntry): InvitationUser[] => {
  if (!entry.id) return [];
  const email = entry.email?.trim() || '';
  const username = entry.username?.trim() || entry.name?.trim() || email;
  if (!username) return [];
  return [
    {
      id: String(entry.id),
      username,
      email,
      firstname: entry.firstname?.trim() || undefined,
      lastname: entry.lastname?.trim() || undefined,
      avatarUrl: entry.avatarUrl?.trim() || undefined,
    },
  ];
};

const formatUserDisplayName = (user: Pick<InvitationUser, 'firstname' | 'lastname'>) =>
  [user.firstname, user.lastname].filter(Boolean).join(' ').trim();

const InvitationModal = ({
  modalVisible,
  setModal,
  eventId,
  eventTitle,
}: InvitationModalProps) => {
  const [form] = Form.useForm<InvitationFormValues>();
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<InvitationUser[]>([]);
  const [foundUsers, setFoundUsers] = useState<InvitationUser[]>([]);
  const [searchTimer, setSearchTimer] = useState<number | null>(null);
  const searchRequestRef = useRef(0);
  const [sendEventInvitation, { loading: sendInvitationLoading }] = useSendEventInvitationMutation();
  const { data: currentUserData } = useUserTokenInfoQuery({ skip: !modalVisible });
  const [runSearchUsers, { loading: usersLoading }] = useSearchUsersLazyQuery({
    fetchPolicy: 'cache-first',
  });

  const currentUserId = currentUserData?.userTokenInfo?.id
    ? String(currentUserData.userTokenInfo.id)
    : undefined;

  const searchSiteUsers = useCallback(
    async (query: string) => {
      const requestId = searchRequestRef.current + 1;
      searchRequestRef.current = requestId;
      try {
        const { data } = await runSearchUsers({
          variables: { search: query.trim(), limit: userSearchLimit },
        });
        // Drop responses for searches the user has already typed past.
        if (searchRequestRef.current !== requestId) return;
        setFoundUsers((data?.usersForAutocomplete ?? []).flatMap(toInvitationUser));
      } catch {
        if (searchRequestRef.current !== requestId) return;
        setFoundUsers([]);
      }
    },
    [runSearchUsers],
  );

  const autoOptions = useMemo<UserAutoOption[]>(() => {
    const selectedIds = new Set(selectedUsers.map((user) => user.id));

    return foundUsers
      .filter((user) => user.id !== currentUserId)
      .filter((user) => !selectedIds.has(user.id))
      .slice(0, userOptionLimit)
      .map((user) => {
        const avatarSrc = (user.avatarUrl || '').trim();
        const displayName = formatUserDisplayName(user);
        return {
          value: user.username,
          userId: user.id,
          user,
          label: (
            <ContactOptionRow>
              <Avatar
                size={24}
                src={avatarSrc || undefined}
                icon={!avatarSrc ? <UserOutlined /> : undefined}
              />
              <ContactOptionText>
                <ContactOptionName>{user.username}</ContactOptionName>
                {displayName ? <ContactOptionMeta>{displayName}</ContactOptionMeta> : null}
              </ContactOptionText>
            </ContactOptionRow>
          ),
        };
      });
  }, [currentUserId, foundUsers, selectedUsers]);

  useEffect(() => {
    if (!modalVisible) {
      return;
    }
    form.resetFields();
    form.setFieldsValue({
      siteMember: '',
      email: { currentValue: '', valueLists: [] },
      subject: eventTitle ? `Invitation: ${eventTitle}` : '',
      message: '',
    });
    setIsValidEmail(false);
    setSelectedUsers([]);
    setFoundUsers([]);
    searchRequestRef.current += 1;
  }, [modalVisible, eventTitle, form]);

  const addSelectedUser = useCallback(
    (user: InvitationUser) => {
      setSelectedUsers((prev) => {
        if (prev.some((item) => item.id === user.id)) {
          return prev;
        }
        return [...prev, user];
      });
      form.setFieldsValue({ siteMember: '' });
      form.setFields([{ name: 'siteMember', errors: [] }]);
    },
    [form],
  );

  const removeSelectedUser = useCallback((userId: string) => {
    setSelectedUsers((prev) => prev.filter((user) => user.id !== userId));
  }, []);

  const collectInvitationRecipients = (values: InvitationFormValues) => {
    const users = selectedUsers
      .map((user) => Number(user.id))
      .filter((id) => Number.isInteger(id) && id > 0);

    const emails = [...(values.email?.valueLists ?? [])];

    const pendingEmail = values.email?.currentValue?.trim();
    if (pendingEmail) {
      if (!emailValidator(pendingEmail)) {
        throw new Error('Email is incorrect format!');
      }
      emails.push(pendingEmail);
    }

    return {
      users,
      emails: Array.from(new Set(emails.map((email) => email.trim().toLowerCase()))),
    };
  };

  const onFinish = async (values: InvitationFormValues) => {
    try {
      const { users, emails } = collectInvitationRecipients(values);
      if (!users.length && !emails.length) {
        message.error('Add at least one site member or email address.');
        return;
      }

      await sendEventInvitation({
        variables: {
          eventId,
          input: {
            users: users.length ? users : undefined,
            emails: emails.length ? emails : undefined,
            message: values.message,
            subject: values.subject,
          },
        },
      });
      setModal?.(false);
      form.resetFields();
      setSelectedUsers([]);
      message.success('Invitation sent successfully.');
    } catch (err) {
      if (err instanceof Error) {
        if (err.message === 'Email is incorrect format!') {
          form.setFields([{ name: 'email', errors: [err.message] }]);
          setIsValidEmail(false);
          return;
        }
        message.error(err.message);
      }
    }
  };

  return (
    <>
      <ContactDropdownGlobalStyle />
      <ConfigProvider theme={contactModalLightTheme}>
        <BaseModal
          open={modalVisible}
          onCancel={() => setModal?.(false)}
          footer={null}
          centered
          destroyOnClose
          width={480}
          getContainer={() => document.getElementById('root') || document.body}
          maskClosable
        >
          <InvitationBody>
            <InvitationHeading>Send Invitation</InvitationHeading>
            <InvitationSubheading>
              Search by username or name to add site members, or invite guests by email.
            </InvitationSubheading>
          </InvitationBody>
          <StyledInvitationForm
            layout="vertical"
            form={form}
            preserve={false}
            onFinish={(values: unknown) => void onFinish(values as InvitationFormValues)}
            validateTrigger="onChange"
          >
            <Form.Item label="Site members" name="siteMember" initialValue="">
              <SiteMemberPicker>
                <AutoComplete
                  className="contact-autocomplete"
                  popupClassName="contact-modal-autocomplete-dropdown"
                  options={autoOptions}
                  placeholder="Type username or name"
                  filterOption={false}
                  notFoundContent={usersLoading ? 'Loading users...' : 'No users found'}
                  onSearch={(text) => {
                    if (searchTimer) window.clearTimeout(searchTimer);
                    const timer = window.setTimeout(() => {
                      searchSiteUsers(text || '');
                    }, 250);
                    setSearchTimer(timer);
                  }}
                  onFocus={() => {
                    searchSiteUsers(String(form.getFieldValue('siteMember') || ''));
                  }}
                  onSelect={(_, option) => {
                    const selected = (option as UserAutoOption).user;
                    if (selected) {
                      addSelectedUser(selected);
                    }
                  }}
                  onChange={(text) => {
                    form.setFieldsValue({ siteMember: text });
                    form.setFields([{ name: 'siteMember', errors: [] }]);
                  }}
                >
                  <Input className="auth-input-text" />
                </AutoComplete>
              </SiteMemberPicker>
              {selectedUsers.length > 0 && (
                <SelectedUsersWrap>
                  {selectedUsers.map((user) => {
                    const label = user.username;
                    const displayName = formatUserDisplayName(user);
                    const isLongTag = label.length > 24;
                    const tag = (
                      <Tag
                        key={user.id}
                        color="#1a2744"
                        closable
                        onClose={() => removeSelectedUser(user.id)}
                        style={{ borderRadius: 5, marginBottom: 6 }}
                      >
                        {label}
                      </Tag>
                    );
                    return isLongTag ? (
                      <Tooltip title={displayName ? `${label} (${displayName})` : label} key={user.id}>
                        {tag}
                      </Tooltip>
                    ) : (
                      tag
                    );
                  })}
                </SelectedUsersWrap>
              )}
            </Form.Item>

            <Form.Item
              label="Email addresses"
              name="email"
              initialValue={{
                currentValue: '',
                valueLists: [],
              }}
              rules={[
                () => ({
                  validator(_, value: InvitationFormValues['email']) {
                    if (!value?.currentValue) {
                      setIsValidEmail(true);
                      return Promise.resolve();
                    }
                    if (emailValidator(value.currentValue)) {
                      setIsValidEmail(true);
                      return Promise.resolve();
                    }
                    setIsValidEmail(false);
                    return Promise.reject(new Error('Email is incorrect format!'));
                  },
                }),
              ]}
            >
              <ListItemInput
                className="invitation-email-input"
                isValid={isValidEmail}
                placeholder="Add email address"
              />
            </Form.Item>

            <Form.Item
              label="Subject"
              name="subject"
              rules={[{ required: true, message: 'Please input the subject of the invitation!' }]}
            >
              <Input className="auth-text-input" placeholder="Invitation subject" />
            </Form.Item>
            <Form.Item
              label="Invitation Message"
              name="message"
              rules={[{ required: true, message: 'Please fill invitation message ...' }]}
            >
              <TextArea
                className="auth-textarea-input"
                autoSize={{ minRows: 3, maxRows: 5 }}
                placeholder="Write your invitation message"
              />
            </Form.Item>
          </StyledInvitationForm>
          <InvitationFooter>
            <Button type="default" onClick={() => setModal?.(false)}>
              Cancel
            </Button>
            <Button type="primary" onClick={() => form.submit()} loading={sendInvitationLoading}>
              Send Invitation
            </Button>
          </InvitationFooter>
        </BaseModal>
      </ConfigProvider>
    </>
  );
};

export default InvitationModal;

const InvitationBody = styled.div`
  margin-bottom: 16px;
`;

const InvitationHeading = styled.h2`
  margin: 0 0 8px;
  font-family: 'Playfair Display', Georgia, 'Times New Roman', serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${colors.grey900};
  line-height: 1.2;
`;

const InvitationSubheading = styled.p`
  margin: 0;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
  font-size: 0.9375rem;
  color: #667085;
  line-height: 1.4;
`;

const SelectedUsersWrap = styled.div`
  margin-top: 10px;
`;

const SiteMemberPicker = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .contact-autocomplete {
    flex: 1;
    min-width: 0;
  }

  .contact-autocomplete .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    height: auto !important;
    background: transparent !important;
  }

  .contact-autocomplete.ant-select-focused .ant-select-selector {
    box-shadow: none !important;
  }
`;

const StyledInvitationForm = styled(Form)`
  .ant-form-item {
    margin-bottom: 18px;
  }

  .ant-form-item-label > label {
    color: #4b5563;
    font-weight: 600;
    font-size: 0.875rem;
    height: auto;
    line-height: 1.3;
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
  }

  .ant-form-item-label {
    padding-bottom: 6px;
  }

  .contact-autocomplete {
    width: 100%;
  }

  .contact-autocomplete .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    height: auto !important;
    background: transparent !important;
  }

  .contact-autocomplete.ant-select-focused .ant-select-selector {
    box-shadow: none !important;
  }

  .auth-input-text.ant-input,
  .auth-text-input.ant-input,
  .auth-textarea-input.ant-input {
    width: 100%;
    border-radius: 8px;
    border: 1px solid #e4e7ec;
    background: #fff;
    color: ${colors.grey900};
    font-size: 0.9375rem;

    &:hover {
      border-color: #1a2744;
    }

    &:focus,
    &.ant-input-focused {
      border-color: #1a2744;
      box-shadow: 0 0 0 3px rgba(26, 39, 68, 0.18);
    }

    &::placeholder {
      color: #9ca3af;
    }
  }

  .auth-textarea-input.ant-input {
    padding: 8px 12px;
  }

  .auth-input-text.ant-input,
  .auth-text-input.ant-input {
    height: 40px;
    padding: 8px 12px;
  }
`;

const InvitationFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;
